import ACCOUNT_ICONS from './accountIcons';

export const ACCOUNT_MENU_METRICS = Object.freeze({
    radius: 24,
    minWidth: 264,
    maxWidth: 'min(360px, calc(100% - 32px))',
    shadow: '0 12px 32px rgba(22, 22, 22, 0.12)',
    triggerIconSize: 32,
    triggerChevronSize: 16,
    itemIconSize: 20,
    badgeSize: 28,
    badgeIconSize: 20,
    pointerSize: 8,
    pointerOffsetFromRight: 28,
});

export const ACCOUNT_PANEL_METRICS = Object.freeze({
    shadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
    rowIconSize: 22,
    chevronSize: 18,
    appearanceOptionMinHeight: 44,
});

export const ACCOUNT_ITEM_RADIUS = 16;

const ACCOUNT_MENU_TYPOGRAPHY = Object.freeze({
    holderName: Object.freeze({ fontSize: 14, fontWeight: 600, lineHeight: 1.43 }),
    holderEmail: Object.freeze({ fontSize: 13, fontWeight: 400, lineHeight: 1.38 }),
    roleLabel: Object.freeze({
        fontSize: 13,
        fontWeight: 600,
        lineHeight: 1.38,
        letterSpacing: '0.01em',
    }),
    item: Object.freeze({ fontSize: 14, fontWeight: 500, lineHeight: 1.43 }),
    panelRow: Object.freeze({
        fontSize: 14,
        fontWeight: 500,
        lineHeight: 1.43,
        letterSpacing: '0.01em',
    }),
});

export function accountTypography(theme, style) {
    const { fontSize, ...type } = ACCOUNT_MENU_TYPOGRAPHY[style];

    return { fontSize: theme.typography.pxToRem(fontSize), ...type };
}

export const ACCOUNT_ROLE_ADMINISTRATOR = 'administrator';
export const ACCOUNT_ROLE_OPERATOR = 'operator';
export const ACCOUNT_ROLE_BUYER = 'buyer';

export const ACCOUNT_ROLES = Object.freeze([ACCOUNT_ROLE_ADMINISTRATOR, ACCOUNT_ROLE_OPERATOR, ACCOUNT_ROLE_BUYER]);

export function normalizeAccountRole(value) {
    return ACCOUNT_ROLES.includes(value) ? value : ACCOUNT_ROLE_BUYER;
}

export const ACCOUNT_ROLE_ICONS = Object.freeze({
    [ACCOUNT_ROLE_ADMINISTRATOR]: ACCOUNT_ICONS.shieldPerson,
    [ACCOUNT_ROLE_OPERATOR]: ACCOUNT_ICONS.badge,
    [ACCOUNT_ROLE_BUYER]: ACCOUNT_ICONS.accountCircle,
});

const LIGHT_SCRIM = 'rgba(255, 255, 255, 0.22)';
const DARK_SCRIM = 'rgba(0, 0, 0, 0.14)';

export const ACCOUNT_ROLE_TREATMENTS = Object.freeze({
    [ACCOUNT_ROLE_ADMINISTRATOR]: Object.freeze({
        bgcolor: 'error.main',
        color: 'error.contrastText',
        badgeBackground: LIGHT_SCRIM,
    }),
    [ACCOUNT_ROLE_OPERATOR]: Object.freeze({
        bgcolor: 'warning.main',
        color: 'warning.contrastText',
        badgeBackground: DARK_SCRIM,
    }),
    [ACCOUNT_ROLE_BUYER]: Object.freeze({
        bgcolor: 'brandSurface.main',
        color: 'brandSurface.contrastText',
        badgeBackground: LIGHT_SCRIM,
    }),
});

export const HIGH_CONTRAST_ACCOUNT_ROLE_TREATMENT = Object.freeze({
    bgcolor: 'text.primary',
    color: 'background.default',
    badgeBackground: 'transparent',
});

export const ACCOUNT_ITEM_MY_TICKETS = 'my-tickets';
export const ACCOUNT_ITEM_MANAGE_EVENTS = 'manage-events';
export const ACCOUNT_ITEM_USER_SETTINGS = 'user-settings';
export const ACCOUNT_ITEM_LOGOUT = 'logout';
export const ACCOUNT_ITEM_STOP_IMPERSONATING = 'stop-impersonating';

export const ACCOUNT_ITEM_ORDER = Object.freeze([
    ACCOUNT_ITEM_MY_TICKETS,
    ACCOUNT_ITEM_MANAGE_EVENTS,
    ACCOUNT_ITEM_USER_SETTINGS,
    ACCOUNT_ITEM_LOGOUT,
    ACCOUNT_ITEM_STOP_IMPERSONATING,
]);

export const ACCOUNT_ITEM_TEXT_COLOR = 'grey.700';
export const ACCOUNT_CHEVRON_COLOR = 'grey.300';
export const ACCOUNT_SELECTED_OPTION_COLOR = 'selection.bg';

const ACCOUNT_SESSION_ITEMS = Object.freeze([ACCOUNT_ITEM_LOGOUT, ACCOUNT_ITEM_STOP_IMPERSONATING]);

export const ACCOUNT_ITEM_TREATMENTS = Object.freeze({
    [ACCOUNT_ITEM_MY_TICKETS]: Object.freeze({
        icon: ACCOUNT_ICONS.confirmationNumber,
        iconColor: 'primary.text',
    }),
    [ACCOUNT_ITEM_MANAGE_EVENTS]: Object.freeze({
        icon: ACCOUNT_ICONS.addCircle,
        iconColor: 'primary.dark',
    }),
    [ACCOUNT_ITEM_USER_SETTINGS]: Object.freeze({
        icon: ACCOUNT_ICONS.settings,
        iconColor: 'grey.600',
    }),
    [ACCOUNT_ITEM_LOGOUT]: Object.freeze({
        icon: ACCOUNT_ICONS.logout,
        iconColor: 'grey.500',
    }),
    [ACCOUNT_ITEM_STOP_IMPERSONATING]: Object.freeze({
        icon: ACCOUNT_ICONS.switchAccount,
        iconColor: 'grey.500',
    }),
});

export function isAccountSessionItem(key) {
    return ACCOUNT_SESSION_ITEMS.includes(key);
}

export function startsAccountSessionGroup(items, index) {
    return index > 0 && isAccountSessionItem(items[index].key) && !isAccountSessionItem(items[index - 1].key);
}

export function buildAccountMenuItems(targetsByItem) {
    return ACCOUNT_ITEM_ORDER
        .filter(key => Boolean(targetsByItem[key]))
        .map(key => ({
            key,
            target: targetsByItem[key],
            ...ACCOUNT_ITEM_TREATMENTS[key],
        }));
}

export function describeAccountHolder(fullName, email) {
    const name = (fullName || '').trim();
    const address = (email || '').trim();

    if (name === '') {
        return { primary: address, secondary: '' };
    }

    return { primary: name, secondary: address };
}
